// opervex-frontend/src/pages/MaterialManagement/Product.js
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Container, Row, Col, Form, Table, InputGroup, Card, Image, Button } from "react-bootstrap";
import { FaSearch, FaUpload, FaPlus, FaSave, FaPencilAlt, FaTrash } from "react-icons/fa";
import { MdOutlineCancel } from "react-icons/md";

import { useAuth } from "../../contexts/AuthContext";
import { SearchHelpModal } from "../../utils/SearchHelp";
import useOpervex from "../../hooks/useOpervex";
import ConfirmService from "../../components/ConfirmationModal/ConfirmService";

const rolePageRequire = "PRODUCTS";

const dataDefault = {
  id: "",
  description: "",
  category: null,
  subcategory: null,
  unitMeasurement: "",
  manufacturer: "",
  model: "",
  externalCode: "",
  controlSerial: false,
  controlMac: false,
  disabled: false,
  existGm: false,
  unitsMeasure: [],
  unitsMeasureDelete: [],
  image: {
    type: "",
    name: "",
    length: 0,
    base64: "",
  },
};

const actionsDefault = {
  create: true,
  change: true,
  delete: true,
  save: true,
};

const Product = () => {
  const { roles, loadingGlobal } = useAuth();
  const [disabledField, setDisabledField] = useState(true);
  const [actions, setActions] = useState(actionsDefault);
  const [data, setData] = useState(dataDefault);
  const [unitsMeasure, setUnitsMeasure] = useState([]);
  const [categories, setCategories] = useState([]);
  const { id } = useParams();
  const OpervexAPI = useOpervex();

  useEffect(() => {
    const fetchData = async () => {
      commandAction({ ...actionsDefault, create: false });

      const units = await OpervexAPI.MaterialManagement.UnitsMeasure.findAll();
      const categs = await OpervexAPI.MaterialManagement.Categories.findAll();

      setUnitsMeasure(units);
      setCategories(categs);

      if (id) await searchAPI(id, units);
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loadingGlobal) {
    return <div>Carregando...</div>;
  }

  const commandAction = (actions) => {
    const hasPermission = roles.find((role) => role.command === rolePageRequire);

    if (!hasPermission) {
      setActions(actionsDefault);
      return;
    }

    if (!actions.create) actions.create = !hasPermission.actions.some((action) => action.key === "Create");
    if (!actions.change) actions.change = !hasPermission.actions.some((action) => action.key === "Change");

    setActions({ ...actions });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    var newData = { ...data };

    switch (name) {
      case "category":
        newData.category = Number(value);
        newData.subcategory = null;
        break;
      case "subcategory":
        newData.subcategory = Number(value);
        break;
      case "unitMeasurement":
        newData.unitMeasurement = value;

        if (!newData.unitsMeasure) newData.unitsMeasure = [];
        if (!newData.unitsMeasureDelete) newData.unitsMeasureDelete = [];

        // Captura os registros base que serão removidos
        const removedBases = newData.unitsMeasure.filter((x) => x.base === true && x.unit !== value);

        // Adiciona ao delete (evitando duplicados)
        removedBases.forEach((item) => {
          if (!newData.unitsMeasureDelete.some((x) => x.unit === item.unit)) {
            newData.unitsMeasureDelete.push(item);
          }
        });

        // Remove os registros base
        newData.unitsMeasure = newData.unitsMeasure.filter((x) => x.base !== true);

        // Procura a unidade selecionada
        const unitIndex = newData.unitsMeasure.findIndex((x) => x.unit === value);

        if (unitIndex !== -1) {
          newData.unitsMeasure[unitIndex] = {
            ...newData.unitsMeasure[unitIndex],
            denominator: 1,
            counter: 1,
            base: true,
          };

          // Se estava marcada para exclusão, remove do delete
          newData.unitsMeasureDelete = newData.unitsMeasureDelete.filter((x) => x.unit !== value);
        } else {
          newData.unitsMeasure.push({
            unit: value,
            description: unitsMeasure?.find((x) => x.id === value)?.description,
            denominator: 1,
            counter: 1,
            base: true,
          });
        }

        break;
      default:
        newData = { ...newData, [name]: type === "checkbox" ? checked : value };
        break;
    }

    setData(newData);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];

    if (file) {
      const reader = new FileReader();

      reader.onloadend = () => {
        const base64String = reader.result.replace(/^data:image\/[a-zA-Z0-9+-]+;base64,/, ""); // Remove qualquer prefixo de imagem Base64

        setData((prev) => ({
          ...prev,
          image: {
            ...prev.image,
            base64: base64String,
            type: file.type,
            length: file.size,
            name: file.name,
          },
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  // CRUD Handlers
  const handleButtonCreate = () => {
    data.unitMeasurement = "";

    setData(dataDefault);
    commandAction({ ...actionsDefault, save: false });
    setDisabledField(false);
  };

  const handleButtonCancel = () => {
    if (data.id === "") {
      data.unitMeasurement = null;

      setData(dataDefault);
      setDisabledField(true);
      commandAction({ ...actionsDefault, create: false });
    } else {
      searchAPI(data.id);
    }
  };

  const handleButtonSave = async () => {
    const body = {
      ...data,
      unitsMeasureDelete: (data.unitsMeasureDelete || []).filter(
        (deleted) => !data.unitsMeasure.some((current) => current.unit === deleted.unit),
      ),
    };

    if (!body.image.base64) delete body.image;

    if (data.id) {
      const result = await OpervexAPI.MaterialManagement.Products.update(body.id, body);

      if (result.errors) {
        await ConfirmService.show("Avisos", result.errors, true);
        return;
      }

      await searchAPI(data.id);

      await ConfirmService.show("Avisos", "Produto atualizado com sucesso!", true);
    } else {
      const result = await OpervexAPI.MaterialManagement.Products.create(body);

      if (result.errors) {
        await ConfirmService.show("Avisos", result.errors, true);
        return;
      }

      await searchAPI(result.id);

      await ConfirmService.show("Avisos", "Produto criado com sucesso!", true);
    }
  };

  const handleButtonSearch = async () => {
    const item = await SearchHelpModal({
      urlData: "/MaterialManagement/Product/SearchHelp",
      schema: await OpervexAPI.MaterialManagement.Products.schema(),
    });

    if (item && item.id) await searchAPI(item.id);
  };

  const handleButtonChange = async () => {
    setDisabledField(false);
    commandAction({ ...actionsDefault, save: false });
  };

  const searchAPI = async (id, units = []) => {
    setDisabledField(true);

    const unitsMeasureAux = units.length ? units : unitsMeasure;

    if (!id) return;

    var result = await OpervexAPI.MaterialManagement.Products.findId(id);

    if (result) {
      // eslint-disable-next-line array-callback-return
      result?.unitsMeasure?.map((unitMeasure) => {
        const unit = unitsMeasureAux.find((x) => x.id === unitMeasure.unit);

        if (unit) unitMeasure.description = unit.description;
        else unitMeasure.description = "";
      });

      setData(result);

      commandAction({ ...actionsDefault, create: false, change: false });
    } else {
      setData(dataDefault);
      commandAction({ ...actionsDefault, create: false });

      await ConfirmService.show("Avisos", "Produto não encontrado!", true);
    }
  };

  const handleChangeUnitsMeasure = async (index, e) => {
    const { name, value } = e.target;
    const upperValue = typeof value === "string" ? value.toUpperCase() : value;

    if (name === "unit") {
      const exists = data.unitsMeasure.some((x, i) => i !== index && x.unit === upperValue);

      if (exists) {
        await ConfirmService.show("Avisos", `Unidade de conversão <b>${upperValue}</b> já cadastrada`, true);
        return;
      }
    }

    setData((prev) => ({
      ...prev,
      unitsMeasure: prev.unitsMeasure.map((row, i) => {
        if (i !== index) return row;

        const newRow = {
          ...row,
          [name]: upperValue,
        };

        if (name === "unit") {
          newRow.description = unitsMeasure?.find((x) => x.id === upperValue)?.description || "";
        }

        return newRow;
      }),
    }));
  };

  const handleUnitMeasure = async ({ index }) => {
    const parameters = await OpervexAPI.MaterialManagement.UnitsMeasure.SearchHelpModal({ baseUnit: data.unitMeasurement });

    const item = await SearchHelpModal({
      urlData: parameters.url,
      preFilters: parameters.preFilters,
      noFilter: true,
      schema: parameters.schema,
    });

    if (!item || !item?.id) return;

    await handleChangeUnitsMeasure(index, { target: { name: "unit", value: item.id } });
  };

  const handleRemoveUnitsMeasure = (index) => {
    setData((prev) => {
      const removedItem = prev.unitsMeasure[index];

      return {
        ...prev,
        unitsMeasure: prev.unitsMeasure.filter((_, i) => i !== index),
        unitsMeasureDelete: removedItem?.unit ? [...(prev.unitsMeasureDelete || []), removedItem] : prev.unitsMeasureDelete,
      };
    });
  };

  const addRow = () => {
    setData((prev) => ({
      ...prev,
      unitsMeasure: [...prev.unitsMeasure, { unit: "", description: "", denominator: 1, counter: 1, base: false }],
    }));
  };

  return (
    <>
      <Container className="mt-2">
        <Form>
          {/* ----- Header -----*/}
          <Card className="mb-2">
            <Card.Header>
              <Row className="d-flex justify-content-between">
                <Col md={8}>
                  <h5>Cabeçalho</h5>
                </Col>
                <Col md={4} className="text-end">
                  <Button variant="success" size="sm" className="me-2" onClick={handleButtonCreate} hidden={actions.create}>
                    <FaPlus /> Novo
                  </Button>
                  <Button variant="primary" size="sm" className="me-2" onClick={handleButtonChange} hidden={actions.change}>
                    <FaPencilAlt /> Alterar
                  </Button>
                  <Button variant="danger" size="sm" className="me-2" onClick={handleButtonCancel} hidden={actions.save}>
                    <MdOutlineCancel /> Cancelar
                  </Button>
                  <Button variant="primary" size="sm" className="me-2" onClick={handleButtonSave} hidden={actions.save}>
                    <FaSave /> Salvar
                  </Button>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body>
              <Row className="mb-2">
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Código</Form.Label>
                    <InputGroup>
                      <Form.Control type="text" size="sm" value={data.id} name="id" onChange={handleChange} disabled={true} />

                      <InputGroup.Text size="sm" type="button" onClick={handleButtonSearch}>
                        <FaSearch />
                      </InputGroup.Text>
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col md={8}>
                  <Form.Group>
                    <Form.Label>Descrição</Form.Label>
                    <Form.Control
                      type="text"
                      size="sm"
                      name="description"
                      value={data.description}
                      onChange={handleChange}
                      disabled={disabledField}
                    />
                  </Form.Group>
                </Col>

                <Col md={2}>
                  <Form.Check // prettier-ignore
                    type="checkbox"
                    id="disabled"
                    checked={data.disabled}
                    name="disabled"
                    onChange={handleChange}
                    label="Inativo"
                    disabled={disabledField}
                  />
                  {<br />}
                  <Form.Check // prettier-ignore
                    type="checkbox"
                    id="existGm"
                    name="existGm"
                    onChange={handleChange}
                    checked={data.existGm}
                    label="Movimentação"
                    disabled={true}
                  />
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* ----- Determination -----*/}
          <Card className="mb-2">
            <Card.Header>
              <h5>Determinação</h5>
            </Card.Header>
            <Card.Body>
              <Row className="mb-2">
                <Col md={6}>
                  <Row className="mb-2">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Unidade de Medida Básica</Form.Label>
                        <Form.Select
                          size="sm"
                          value={data.unitMeasurement}
                          onChange={handleChange}
                          name="unitMeasurement"
                          disabled={disabledField || data.existGm}
                        >
                          <option value="">Selecione...</option>
                          {unitsMeasure
                            ?.filter((unitMeasure) => disabledField || unitMeasure.disabled === false || data.existGm)
                            ?.map((unitMeasure) => (
                              <option key={unitMeasure.id} value={unitMeasure.id}>
                                {unitMeasure.description}
                              </option>
                            ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Categoria</Form.Label>
                        <Form.Select
                          size="sm"
                          value={data.category ?? ""}
                          onChange={handleChange}
                          name="category"
                          disabled={disabledField}
                        >
                          <option value={null}>Selecione...</option>
                          {categories
                            ?.filter((category) => disabledField || category.disabled === false)
                            ?.map((category) => (
                              <option key={category.id} value={category.id}>
                                {category.description}
                              </option>
                            ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Sub Categoria</Form.Label>
                        <Form.Select
                          size="sm"
                          value={data.subcategory ?? ""}
                          onChange={handleChange}
                          name="subcategory"
                          disabled={
                            !categories?.find((x) => x.id === Number(data.category))?.subcategories?.length || disabledField
                          }
                        >
                          <option value={null}>Selecione...</option>
                          {categories
                            ?.filter((x) => x.id === Number(data.category))
                            .map((category) =>
                              category.subcategories.map((subcategory) => (
                                <option key={subcategory.id} value={subcategory.id}>
                                  {subcategory.description}
                                </option>
                              )),
                            )}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col md={12}>
                      <Form.Check // prettier-ignore
                        type="checkbox"
                        id="controlSerial"
                        name="controlSerial"
                        checked={data?.controlSerial}
                        onChange={handleChange}
                        label="Controle Nº Série"
                        disabled={disabledField}
                      />
                      <Form.Check // prettier-ignore
                        type="checkbox"
                        name="controlMac"
                        id="controlMac"
                        checked={data?.controlMac}
                        onChange={handleChange}
                        label="Controle MAC"
                        disabled={disabledField}
                      />
                    </Col>
                  </Row>
                </Col>
                <Col md={6}>
                  <Row className="mb-2">
                    <Col md={4}></Col>
                    <Col md={4}>
                      <Form.Group className="d-flex justify-content-center">
                        <span className="border">
                          <Image
                            width={200}
                            height={200}
                            src={data.image.type ? `data:${data.image.type};base64,${data.image.base64}` : ""}
                            name="image"
                            onChange={handleChange}
                            rounded
                          />
                        </span>
                        <Col md={4}></Col>
                        <div className="mb-2">
                          <Button onClick={() => document.getElementById("fileInput").click()} disabled={disabledField}>
                            <FaUpload />
                          </Button>
                          <input
                            type="file"
                            id="fileInput"
                            style={{ display: "none" }}
                            accept="image/*"
                            onChange={(e) => handleImageUpload(e)}
                          />
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* ----- Additional -----*/}
          <Card className="mb-2">
            <Card.Header>
              <h5>Informações Adicionais</h5>
            </Card.Header>
            <Card.Body>
              <Row className="mb-2">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Fabricante</Form.Label>
                    <Form.Control
                      type="text"
                      size="sm"
                      value={data?.manufacturer}
                      onChange={handleChange}
                      name="manufacturer"
                      disabled={disabledField}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Modelo</Form.Label>
                    <Form.Control
                      type="text"
                      size="sm"
                      value={data?.model}
                      onChange={handleChange}
                      name="model"
                      disabled={disabledField}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Código Externo</Form.Label>
                    <Form.Control
                      type="text"
                      size="sm"
                      value={data?.externalCode}
                      onChange={handleChange}
                      name="externalCode"
                      disabled={disabledField}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* ----- Conversion -----*/}
          <Card className="mb-2">
            <Card.Header>
              <h5>Conversão de Unidade de Medida</h5>
            </Card.Header>
            <Card.Body>
              <Row className="mb-2">
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr className="text-center align-middle">
                      <th>
                        Entrada
                        <br />
                        (Denominador)
                      </th>
                      <th>Unidade</th>
                      <th>Descrição</th>
                      <th>Equivalente</th>
                      <th>
                        Saída
                        <br />
                        (Contador)
                      </th>
                      <th>Unidade Base</th>
                      <th>Ação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.unitsMeasure && data.unitsMeasure.length > 0 ? (
                      data.unitsMeasure.map((unitMeasure, index) => (
                        <tr key={index}>
                          <td>
                            <Form.Control
                              size="sm"
                              type="number"
                              name="denominator"
                              value={unitMeasure.denominator}
                              onChange={(e) => handleChangeUnitsMeasure(index, e)}
                              disabled={disabledField || unitMeasure.base}
                            />
                          </td>
                          <td>
                            <InputGroup>
                              <Form.Control
                                type="text"
                                size="sm"
                                name="unit"
                                value={unitMeasure.unit}
                                onChange={(e) => handleChangeUnitsMeasure(index, e)}
                                disabled={disabledField || unitMeasure.base}
                              />
                              <InputGroup.Text
                                size="sm"
                                type="button"
                                hidden={disabledField || unitMeasure.base}
                                onClick={(e) => handleUnitMeasure({ index: index })}
                              >
                                <FaSearch />
                              </InputGroup.Text>
                            </InputGroup>
                          </td>
                          <td className="text-center">{unitMeasure.description}</td>
                          <td className="text-center">equivalente a</td>
                          <td>
                            <Form.Control
                              size="sm"
                              type="number"
                              name="counter"
                              value={unitMeasure.counter}
                              onChange={(e) => handleChangeUnitsMeasure(index, e)}
                              disabled={disabledField || unitMeasure.base}
                            />
                          </td>
                          <td className="text-center">
                            {(() => {
                              const base = data.unitsMeasure?.find((x) => x.base);
                              return base ? `${base.unit} - ${base.description}` : "";
                            })()}
                          </td>
                          <td className="text-center">
                            <Button
                              variant="danger"
                              onClick={() => handleRemoveUnitsMeasure(index)}
                              size="sm"
                              disabled={disabledField || unitMeasure.base}
                            >
                              <FaTrash />
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="text-center">
                          Nenhuma unidade de conversão encontrada
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>

                <Button variant="primary" onClick={addRow} hidden={actions.save}>
                  Adicionar Linha
                </Button>
              </Row>
            </Card.Body>
          </Card>
        </Form>
      </Container>
    </>
  );
};

export default Product;
